import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoAdd,
  IoAlertCircleOutline,
  IoBarcodeOutline,
  IoCubeOutline,
  IoClose,
  IoCheckmarkOutline,
  IoPencilOutline,
} from "react-icons/io5";
import api, { fileUrl } from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { AppHeader } from "@/components/AppHeader";
import { BottomNav } from "@/components/BottomNav";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { ExpiryDateInput } from "@/components/ExpiryDateInput";
import { PhotoPicker } from "@/components/PhotoPicker";
import { MultiAddWizard } from "@/components/MultiAddWizard";
import { formatDateIT } from "@/utils";

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((d - now) / (1000 * 60 * 60 * 24));
};

const productImageSrc = (p) => {
  if (p.image_file_id) return fileUrl(p.image_file_id);
  return p.image_url || "";
};

const emptyForm = { barcode: "", name: "", article_code: "", expiry_date: "", expiry_date_2: "", expiry_date_3: "", quantity: 1, cost: "", price: "", image_url: "", image_file_id: "" };

export default function NearExpiryPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { canAccessStore } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(15);
  const [addOpen, setAddOpen] = useState(false);
  const [multiAddOpen, setMultiAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [quickExpiryOpen, setQuickExpiryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stores/${storeId}/near-expiry-products`, { params: { days } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Accesso negato");
        navigate("/");
      }
    } finally { setLoading(false); }
  }, [storeId, days, navigate]);

  useEffect(() => {
    if (!canAccessStore(storeId)) { navigate("/"); return; }
    load();
  }, [storeId, load, canAccessStore, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onBarcodeDetected = async (code) => {
    setScannerOpen(false);
    set("barcode", code);
    try {
      const { data } = await api.get(`/products/lookup/${encodeURIComponent(code)}`);
      if (data?.name) set("name", data.name);
      if (data?.article_code) set("article_code", data.article_code);
      if (data?.image_url) set("image_url", data.image_url);
      if (data?.image_file_id) set("image_file_id", data.image_file_id);
      if (data?.cost != null) set("cost", data.cost);
      if (data?.price != null) set("price", data.price);
      const isFuzzy = typeof data?.source === "string" && data.source.includes("listino-fuzzy");
      if (isFuzzy && data?.matched_listino_name) {
        toast.success(`Abbinato al listino: "${data.matched_listino_name}". Verifica prima di salvare.`, { duration: 5000 });
      } else {
        toast.success(`Trovato: ${data?.name || code}`);
      }
    } catch {
      // silent fallback: user fills manually
    }
  };

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setAddOpen(true); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.expiry_date) { toast.error("Inserisci la data di scadenza"); return; }
    if (!form.barcode && !form.name?.trim()) { toast.error("Serve almeno un codice a barre o un nome"); return; }
    setSaving(true);
    try {
      // Collect all filled expiry dates (main + optional 2nd + 3rd).
      const dates = [form.expiry_date, form.expiry_date_2, form.expiry_date_3].filter(Boolean);
      // De-duplicate preserving order.
      const uniqueDates = Array.from(new Set(dates));
      // Split the quantity evenly across the different lots, so 6 pezzi with 2 dates
      // become 3+3. Any remainder goes on the first lot.
      const totalQty = Math.max(1, Number(form.quantity) || 1);
      const base = Math.floor(totalQty / uniqueDates.length);
      const remainder = totalQty - base * uniqueDates.length;
      let lastAction = null;
      for (let i = 0; i < uniqueDates.length; i++) {
        const qtyForLot = base + (i === 0 ? remainder : 0);
        const payload = {
          store_id: storeId,
          barcode: form.barcode || "",
          name: form.name?.trim() || undefined,
          expiry_date: uniqueDates[i],
          quantity: qtyForLot,
          cost: form.cost === "" ? undefined : Number(form.cost),
          price: form.price === "" ? undefined : Number(form.price),
          image_url: form.image_url || undefined,
          image_file_id: form.image_file_id || undefined,
          // Near-expiry quick-add: each expiry date becomes its own lot AND the
          // resulting product is hidden from the catalog listing (user requirement:
          // items entered here live only in the Prossime Scadenze section).
          create_new_lot: true,
          hide_from_catalog: true,
        };
        const { data } = await api.post("/products/quick-upsert", payload);
        lastAction = data.action;
      }
      const label = uniqueDates.length > 1
        ? `${uniqueDates.length} lotti aggiunti`
        : (lastAction === "updated" ? "Scadenza aggiornata" : "Prodotto aggiunto");
      toast.success(label);
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const openQuickExpiry = (p) => {
    setEditingId(p.id);
    setForm({ ...emptyForm, expiry_date: p.expiry_date || "", barcode: p.barcode || "", name: p.name || "", quantity: p.quantity ?? 1 });
    setQuickExpiryOpen(true);
  };

  const saveQuickExpiry = async () => {
    if (!editingId || !form.expiry_date) return;
    setSaving(true);
    try {
      await api.put(`/products/${editingId}`, { expiry_date: form.expiry_date });
      toast.success("Scadenza aggiornata");
      setQuickExpiryOpen(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="mobile-frame with-bottom-bar" data-testid="near-expiry-page">
      <AppHeader title={`Prossime Scadenze · ${t(STORE_LABELS[storeId]) || ""}`} showBack backTo={`/catalog/${storeId}`} />
      <div className="tf-content">
        <div className="tf-chips-row" style={{ marginBottom: 12 }}>
          {[4, 7, 15, 30].map((d) => (
            <button
              key={d}
              data-testid={`ne-range-${d}`}
              className={`tf-chip ${days === d ? "active-red" : ""}`}
              onClick={() => setDays(d)}
            >
              ≤ {d} giorni
            </button>
          ))}
        </div>

        {loading ? (
          <div className="tf-empty">{t("loading")}</div>
        ) : items.length === 0 ? (
          <div className="tf-empty">
            <IoAlertCircleOutline size={44} style={{ opacity: 0.4 }} />
            <div>Nessun prodotto in scadenza nei prossimi {days} giorni</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((p) => {
              const dLeft = daysUntil(p.expiry_date);
              const isCritical = dLeft !== null && dLeft <= 4;
              const src = productImageSrc(p);
              return (
                <div key={p.id} data-testid={`ne-item-${p.id}`} className="tf-near-item">
                  {isCritical && <span className="tf-product-alert" style={{ position: "absolute", top: 8, left: 8, width: 24, height: 24, fontSize: 12 }}>!</span>}
                  <div className="tf-near-thumb">
                    {src ? <img src={src} alt={p.name} loading="lazy" /> : <IoCubeOutline size={28} />}
                  </div>
                  <div className="tf-near-body">
                    <div className="tf-near-name">{p.name}</div>
                    <div className="tf-near-meta">
                      <span data-testid={`ne-date-${p.id}`}>{formatDateIT(p.expiry_date)}</span>
                      <span className={`tf-near-days ${isCritical ? "critical" : ""}`}>
                        {dLeft <= 0 ? "OGGI" : `${dLeft} gg`}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tf-icon-btn"
                    data-testid={`ne-edit-${p.id}`}
                    onClick={() => openQuickExpiry(p)}
                    aria-label="Modifica scadenza"
                  >
                    <IoPencilOutline size={18} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button data-testid="ne-fab-add" className="tf-fab" onClick={() => setMultiAddOpen(true)}>
        <IoAdd size={22} /> Aggiunta prodotti
      </button>

      <MultiAddWizard
        open={multiAddOpen}
        storeId={storeId}
        onClose={() => { setMultiAddOpen(false); load(); }}
      />

      <BottomNav />

      {addOpen && (
        <div className="tf-sheet-backdrop" onClick={() => !saving && setAddOpen(false)} data-testid="ne-add-sheet">
          <form
            className="tf-sheet"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
          >
            <div className="tf-between" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 17 }}>Aggiunta rapida</div>
              <button type="button" className="tf-close-btn" onClick={() => setAddOpen(false)} disabled={saving}><IoClose size={20} /></button>
            </div>

            <div className="tf-field">
              <label className="tf-label">Codice a barre</label>
              <div className="tf-input-wrap">
                <input
                  className="tf-input tf-input-with-icon-right"
                  value={form.barcode}
                  onChange={(e) => set("barcode", e.target.value)}
                  inputMode="numeric"
                  placeholder="Scansiona o digita"
                  style={{ paddingRight: 60 }}
                  data-testid="ne-add-barcode"
                />
                <button type="button" data-testid="ne-add-barcode-scan" className="tf-icon-btn red" style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", width: 40, height: 40 }} onClick={() => setScannerOpen(true)}>
                  <IoBarcodeOutline size={20} />
                </button>
              </div>
            </div>

            <div className="tf-field">
              <label className="tf-label">Nome prodotto {form.barcode ? "(opzionale se già in catalogo)" : "*"}</label>
              <input className="tf-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ne-add-name" placeholder="Es. Pasta Barilla 500g" />
            </div>

            <div className="tf-field">
              <label className="tf-label">Data di scadenza *</label>
              <ExpiryDateInput value={form.expiry_date} onChange={(v) => set("expiry_date", v)} testIdPrefix="ne-add-expiry" />
              {form.expiry_date && (
                <div style={{ marginTop: 8 }}>
                  <ExpiryDateInput value={form.expiry_date_2} onChange={(v) => set("expiry_date_2", v)} testIdPrefix="ne-add-expiry-2" />
                </div>
              )}
              {form.expiry_date && form.expiry_date_2 && (
                <div style={{ marginTop: 8 }}>
                  <ExpiryDateInput value={form.expiry_date_3} onChange={(v) => set("expiry_date_3", v)} testIdPrefix="ne-add-expiry-3" />
                </div>
              )}
              {form.expiry_date && (
                <div className="tf-small" style={{ marginTop: 6, color: "var(--tf-muted)" }}>
                  Puoi aggiungere fino a 3 date se il pacco contiene lotti diversi. La quantità sarà divisa equamente.
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="tf-field">
                <label className="tf-label">Q.tà</label>
                <input className="tf-input" type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="ne-add-qty" />
              </div>
              <div className="tf-field">
                <label className="tf-label">Costo €</label>
                <input className="tf-input" type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} data-testid="ne-add-cost" />
              </div>
              <div className="tf-field">
                <label className="tf-label">Prezzo €</label>
                <input className="tf-input" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="ne-add-price" />
              </div>
            </div>

            <div className="tf-field">
              <label className="tf-label">Foto (opzionale)</label>
              <PhotoPicker
                fileId={form.image_file_id}
                imageUrl={form.image_url}
                onChange={(fileId) => { set("image_file_id", fileId); set("image_url", ""); }}
                testIdPrefix="ne-add-photo"
              />
            </div>

            <button type="submit" className="tf-btn-primary" disabled={saving} data-testid="ne-add-save">
              <IoCheckmarkOutline size={18} /> {saving ? "Salvataggio…" : "Salva"}
            </button>
          </form>
        </div>
      )}

      {quickExpiryOpen && (
        <div className="tf-sheet-backdrop" onClick={() => !saving && setQuickExpiryOpen(false)} data-testid="ne-quick-expiry-sheet">
          <div className="tf-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="tf-between" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 17 }}>Aggiorna scadenza</div>
              <button type="button" className="tf-close-btn" onClick={() => setQuickExpiryOpen(false)}><IoClose size={20} /></button>
            </div>
            <div style={{ fontSize: 14, color: "var(--tf-muted)", marginBottom: 12 }}>{form.name}</div>
            <div className="tf-field">
              <label className="tf-label">Nuova data di scadenza *</label>
              <ExpiryDateInput value={form.expiry_date} onChange={(v) => set("expiry_date", v)} testIdPrefix="ne-quick-expiry" />
            </div>
            <button type="button" className="tf-btn-primary" disabled={saving || !form.expiry_date} onClick={saveQuickExpiry} data-testid="ne-quick-expiry-save">
              <IoCheckmarkOutline size={18} /> {saving ? "Salvataggio…" : "Salva scadenza"}
            </button>
          </div>
        </div>
      )}

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={onBarcodeDetected} />
    </div>
  );
}
